/**
 * Reports v2 — timeframe → concrete date windows, and the previous-period
 * window for compare. Replaces the mock's PREV_TIMEFRAME self-mapping (which
 * returned byte-identical "previous" numbers for this_week / yesterday /
 * custom). All dates are `YYYY-MM-DD` strings on INDIA's calendar — the server
 * resolves them to IST day boundaries (startOfDayIST/endOfDayIST), never send
 * timestamps. Same convention as useV2DashboardParams on the dashboard.
 */
import type { TimeframeKey } from "@/features/Dashboard/v2/data/types";
import { getTodayYmdIST } from "@/utils/istCalendar";
import { elapsedWindow, thisPeriodWindow } from "@/utils/periodWindow";

export interface DateWindow {
  startDate: string;
  endDate: string;
}

/**
 * Reports-local timeframe keys — a SUPERSET of the dashboard's TimeframeKey.
 * The dashboard type stays frozen (separation of concern); reports own their
 * extra enterprise presets. A base TimeframeKey widens into this, so a saved
 * view that stored a base key still loads. Every key here MUST resolve in
 * resolveReportWindow below (an unresolved key silently falls to all-time).
 */
export type ReportTimeframeKey =
  | TimeframeKey
  | "last_week"
  | "last_7_days"
  | "last_30_days"
  | "last_90_days"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Parse `YYYY-MM-DD` as a LOCAL date. `new Date("YYYY-MM-DD")` parses as UTC
 *  midnight, which shifts a day in any non-UTC zone — never use it here. */
function parseYmd(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(d: Date, days: number): Date {
  const n = new Date(d);
  n.setDate(n.getDate() + days);
  return n;
}

/** First calendar month (0-11) of the quarter containing month `m` (0-11). */
function quarterStartMonth(m: number): number {
  return Math.floor(m / 3) * 3;
}

/** Whole days from a to b, inclusive of both (a ≤ b). */
function daysInclusive(a: Date, b: Date): number {
  const MS = 24 * 60 * 60 * 1000;
  const au = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const bu = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((bu - au) / MS) + 1;
}

/**
 * Resolve a report timeframe to its concrete window. Returns null for "all"
 * (no window → all-time) and for "custom" without both dates.
 *
 * "This week / month / quarter / year" are the WHOLE calendar period (Monday
 * to Sunday, the 1st to the last day, …) — the one rule every surface shares
 * (`utils/periodWindow.ts`; the dashboard bar and the leads page read the same
 * helper). They used to end at today, which read the same for anything dated
 * in the past but left out the rest of the period for anything dated ahead
 * (follow-ups). The compare window still runs on the elapsed part — see
 * `computePrevWindow`.
 */
export function resolveReportWindow(
  timeframe: ReportTimeframeKey,
  customStart?: string | null,
  customEnd?: string | null,
  now: Date = new Date(),
): DateWindow | null {
  // "Today" is INDIA's date, whatever the browser's clock says: the server
  // reads these strings as IST days, so a window built from the machine's own
  // date asked for the wrong day (and month, quarter, year at a boundary) for
  // anyone outside IST. `today` is a local-midnight Date carrying the IST
  // date, so every getDay / month / quarter / year step below is unchanged.
  const [iy, im, id] = getTodayYmdIST(now).split("-").map(Number);
  const today = new Date(iy!, im! - 1, id!);
  switch (timeframe) {
    case "all":
      return null;
    case "today":
      return { startDate: ymd(today), endDate: ymd(today) };
    case "yesterday": {
      const y = addDays(today, -1);
      return { startDate: ymd(y), endDate: ymd(y) };
    }
    case "this_week":
    case "this_month":
    case "this_quarter":
    case "this_year":
      return thisPeriodWindow(timeframe, now);
    case "last_week": {
      const thisMonday = addDays(today, -((today.getDay() + 6) % 7));
      return {
        startDate: ymd(addDays(thisMonday, -7)), // last Monday
        endDate: ymd(addDays(thisMonday, -1)), // last Sunday
      };
    }
    case "last_7_days":
      return { startDate: ymd(addDays(today, -6)), endDate: ymd(today) };
    case "last_30_days":
      return { startDate: ymd(addDays(today, -29)), endDate: ymd(today) };
    case "last_90_days":
      return { startDate: ymd(addDays(today, -89)), endDate: ymd(today) };
    case "last_month":
      return {
        startDate: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        endDate: ymd(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case "last_quarter": {
      // month - 3 normalises across the year boundary (Q1 → prev-year Q4).
      const prevQStart = new Date(
        today.getFullYear(),
        quarterStartMonth(today.getMonth()) - 3,
        1,
      );
      return {
        startDate: ymd(prevQStart),
        endDate: ymd(
          new Date(prevQStart.getFullYear(), prevQStart.getMonth() + 3, 0),
        ),
      };
    }
    case "last_year":
      return {
        startDate: ymd(new Date(today.getFullYear() - 1, 0, 1)),
        endDate: ymd(new Date(today.getFullYear() - 1, 11, 31)),
      };
    case "custom":
      if (customStart && customEnd) {
        const s = parseYmd(customStart);
        const e = parseYmd(customEnd);
        if (s && e && s.getTime() <= e.getTime())
          return { startDate: customStart, endDate: customEnd };
      }
      return null;
    default:
      return null;
  }
}

/**
 * The previous-period window for a resolved current window. Calendar-aware for
 * the calendar presets, length-shift for everything else:
 *
 * - today / yesterday → the day before the window
 * - this_week (Mon..today) → last week, Monday..same weekday
 * - this_month (1st..today) → previous month, 1st..same day-of-month (clamped
 *   to that month's length — Mar 31 compares to Feb 28/29)
 * - last_month → the full month before it
 * - this_quarter (QTD) → previous quarter, same days-from-start (clamped)
 * - last_quarter → the full quarter before it
 * - this_year (YTD) → previous year, Jan 1..same month/day (clamped for Feb 29)
 * - last_year → the full year before it
 * - last_week / last_7_days / last_30_days / last_90_days / custom → shifted
 *   back by their own length (a 10-day range compares to the 10 days before it)
 *
 * Every result is past-anchored by construction. Returns null when the input
 * window is missing/invalid (compare is then disabled — never fabricated).
 *
 * THE COMPARE GUARD: the comparison always runs on the ELAPSED part of the
 * current window (its end capped at today). A "this …" window now reaches the
 * period's real end, but only its days so far hold data — comparing this
 * week's Monday-to-Wednesday against ALL of last week would turn every arrow
 * red until Sunday. So the rules above read "to today" whatever the window's
 * own end is, and a window that has not started yet has nothing to compare.
 */
export function computePrevWindow(
  timeframe: ReportTimeframeKey,
  fullWindow: DateWindow | null,
  now: Date = new Date(),
): DateWindow | null {
  if (!fullWindow) return null;
  const window = elapsedWindow(fullWindow, now);
  if (!window) return null;
  const start = parseYmd(window.startDate);
  const end = parseYmd(window.endDate);
  if (!start || !end || start.getTime() > end.getTime()) return null;

  switch (timeframe) {
    case "this_week": {
      return {
        startDate: ymd(addDays(start, -7)),
        endDate: ymd(addDays(end, -7)),
      };
    }
    case "this_month": {
      const prevFirst = new Date(start.getFullYear(), start.getMonth() - 1, 1);
      const prevMonthDays = new Date(
        start.getFullYear(),
        start.getMonth(),
        0,
      ).getDate();
      const day = Math.min(end.getDate(), prevMonthDays);
      return {
        startDate: ymd(prevFirst),
        endDate: ymd(new Date(prevFirst.getFullYear(), prevFirst.getMonth(), day)),
      };
    }
    case "last_month": {
      const prevFirst = new Date(start.getFullYear(), start.getMonth() - 1, 1);
      const prevLast = new Date(start.getFullYear(), start.getMonth(), 0);
      return { startDate: ymd(prevFirst), endDate: ymd(prevLast) };
    }
    case "this_quarter": {
      // QTD → previous quarter, same number of days from its start (clamped to
      // that quarter's length). start is the quarter's 1st; −3 months → prev
      // quarter (normalises across the year boundary).
      const len = daysInclusive(start, end);
      const prevQStart = new Date(start.getFullYear(), start.getMonth() - 3, 1);
      const prevQEnd = new Date(
        prevQStart.getFullYear(),
        prevQStart.getMonth() + 3,
        0,
      );
      const cand = addDays(prevQStart, len - 1);
      return {
        startDate: ymd(prevQStart),
        endDate: ymd(cand.getTime() <= prevQEnd.getTime() ? cand : prevQEnd),
      };
    }
    case "last_quarter": {
      const prevQStart = new Date(start.getFullYear(), start.getMonth() - 3, 1);
      return {
        startDate: ymd(prevQStart),
        endDate: ymd(
          new Date(prevQStart.getFullYear(), prevQStart.getMonth() + 3, 0),
        ),
      };
    }
    case "this_year": {
      // YTD → previous year, Jan 1..same month/day (clamp Feb 29 → Feb 28).
      const py = start.getFullYear() - 1;
      const prevMonthDays = new Date(py, end.getMonth() + 1, 0).getDate();
      return {
        startDate: ymd(new Date(py, 0, 1)),
        endDate: ymd(
          new Date(py, end.getMonth(), Math.min(end.getDate(), prevMonthDays)),
        ),
      };
    }
    case "last_year": {
      const py = start.getFullYear() - 1;
      return {
        startDate: ymd(new Date(py, 0, 1)),
        endDate: ymd(new Date(py, 11, 31)),
      };
    }
    default: {
      // last_week / last_N_days / today / yesterday / custom / any fixed-length
      // window: shift back by its own length.
      const len = daysInclusive(start, end);
      const prevEnd = addDays(start, -1);
      const prevStart = addDays(prevEnd, -(len - 1));
      return { startDate: ymd(prevStart), endDate: ymd(prevEnd) };
    }
  }
}
